import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertTriangle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { approachIntro, integrativeTherapy, briefTherapy } from '../data/approachContent';

const Approach = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F3EB]">
      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[#F5F3EB] -z-10"></div>
        <div className="max-w-6xl mx-auto">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Link
              to="/qui-suis-je"
              className="inline-flex items-center text-sm font-medium text-[#4A4458] hover:text-[#21167D] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à « Qui suis-je ? »
            </Link>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#21167D] mb-4 text-center">
              {approachIntro.heading}
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mt-10">
            <ul className="space-y-4">
              {approachIntro.items.map((item, index) => (
                <li
                  key={index}
                  className="p-5 bg-[#FDFBF5] rounded-xl shadow-sm border border-[#DCB071]/30 text-lg font-medium text-[#21167D]"
                >
                  {item}
                </li>
              ))}
            </ul>
            <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-[#F5F3EB]">
              <img
                src={approachIntro.image.src}
                alt={approachIntro.image.alt}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section F — Psychothérapie intégrative */}
      <section className="py-16 px-4 bg-[#FDFBF5]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-3xl font-display font-bold text-[#DCB071]">{integrativeTherapy.number}</span>
            <h2 className="font-display text-3xl font-semibold text-[#21167D]">{integrativeTherapy.heading}</h2>
          </div>
          <p className="text-lg text-[#4A4458] leading-relaxed mb-8">{integrativeTherapy.intro}</p>

          <h3 className="font-accent font-semibold text-xl text-[#21167D] mb-4">
            {integrativeTherapy.objectivesHeading}
          </h3>
          <ul className="space-y-3 mb-8">
            {integrativeTherapy.objectives.map((objective, index) => (
              <li key={index} className="flex items-start gap-3 text-[#4A4458]">
                <span className="mt-2 w-2 h-2 rounded-full bg-[#EAA93D] flex-shrink-0"></span>
                <span>{objective}</span>
              </li>
            ))}
          </ul>

          <Card className="bg-[#21167D] text-[#F5F3EB] border-0 shadow-lg">
            <CardContent className="p-8 flex gap-4">
              <Quote className="w-8 h-8 text-[#EAA93D] flex-shrink-0" />
              <blockquote className="font-accent italic text-xl leading-relaxed">
                « {integrativeTherapy.quote} »
              </blockquote>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section G — Thérapies brèves */}
      <section className="py-16 px-4 bg-[#DCB071]/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-3xl font-display font-bold text-[#DCB071]">{briefTherapy.number}</span>
            <h2 className="font-display text-3xl font-semibold text-[#21167D]">{briefTherapy.heading}</h2>
          </div>
          <div className="space-y-5 text-lg text-[#4A4458] leading-relaxed mb-8">
            <p>{briefTherapy.intro}</p>
            <p>{briefTherapy.paragraph2}</p>
          </div>

          <div className="flex items-start gap-4 p-6 bg-[#EAA93D]/25 border border-[#EAA93D] rounded-2xl mb-8">
            <AlertTriangle className="w-6 h-6 text-[#21167D] flex-shrink-0 mt-1" />
            <p className="text-[#21167D] leading-relaxed">{briefTherapy.warning}</p>
          </div>

          <Card className="bg-[#21167D] text-[#F5F3EB] border-0 shadow-lg">
            <CardContent className="p-8 flex gap-4">
              <Quote className="w-8 h-8 text-[#EAA93D] flex-shrink-0" />
              <blockquote className="font-accent italic text-xl leading-relaxed">
                « {briefTherapy.quote} »
              </blockquote>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-[#21167D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl font-semibold text-[#F5F3EB] mb-6">
            Prêt·e à commencer votre cheminement ?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rendez-vous">
              <Button size="lg" className="bg-[#EAA93D] text-[#21167D] hover:bg-[#F5F3EB] hover:text-[#21167D] shadow-xl">
                Prendre rendez-vous
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-[#F5F3EB] text-[#F5F3EB] hover:bg-[#F5F3EB] hover:text-[#21167D]">
                Découvrir mes services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Approach;
